import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button, Dialog, DialogPanel, TextInput } from "@tremor/react";
import { toast } from "sonner";
import { fetchLocations, fetchMonitors, updateMonitor } from "../../api/monitors.js";
import {
  getMonitorChannels,
  listChannels,
  setMonitorChannels,
} from "../../api/channels.js";
import { listEscalationPolicies } from "../../api/oncall.js";
import {
  IntervalSelect,
  HttpOptions,
  SslOptions,
  DomainOptions,
  DnsOptions,
  TcpOptions,
  PlaywrightOptions,
  ShopifyOptions,
  WordPressOptions,
  PingOptions,
  PortOptions,
  ApiOptions,
  CronOptions,
  SeoHealthOptions,
  BrokenLinksOptions,
  ImageOptimizationOptions,
  PerformanceBudgetOptions,
  SuspiciousScriptOptions,
  CarbonFootprintOptions,
} from "./MonitorTypeOptions.jsx";

function EditMonitorPanel({ monitor, onClose, onSaved }) {
  const { t } = useTranslation("monitor_detail");
  const [form, setForm] = useState({
    name: monitor.name,
    target: monitor.target,
    interval_seconds: monitor.interval_seconds,
    locations: monitor.locations ?? [],
    options: monitor.options ?? {},
    escalation_policy_id: monitor.escalation_policy_id ?? "",
    depends_on_monitor_id: monitor.depends_on_monitor_id ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [otherMonitors, setOtherMonitors] = useState([]);

  useEffect(() => {
    fetchLocations()
      .then(setAvailableLocations)
      .catch(() => {});
  }, []);

  useEffect(() => {
    async function loadGroups() {
      try {
        const [groups, assigned] = await Promise.all([
          listChannels(),
          getMonitorChannels(monitor.id),
        ]);
        setAllGroups(groups);
        setSelectedGroupIds((assigned.channel_ids ?? []).map(String));
      } catch {
        // non-critical, ignore
      }
    }

    async function loadPolicies() {
      try {
        const data = await listEscalationPolicies();
        setPolicies(data);
      } catch {
        // non-critical — business feature, may return 402
      }
    }

    async function loadMonitors() {
      try {
        const res = await fetchMonitors({ limit: 100 });
        setOtherMonitors((res.data || []).filter((m) => m.id !== monitor.id));
      } catch {
        // non-critical
      }
    }

    loadGroups();
    loadPolicies();
    loadMonitors();
  }, [monitor.id]);

  function toggleGroup(id) {
    const sid = String(id);
    setSelectedGroupIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  }

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setOpt(key, value) {
    setForm((prev) => ({
      ...prev,
      options: { ...prev.options, [key]: value },
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        escalation_policy_id: form.escalation_policy_id || null,
        depends_on_monitor_id: form.depends_on_monitor_id || null,
      };
      const updated = await updateMonitor(monitor.id, payload);
      await setMonitorChannels(monitor.id, selectedGroupIds);
      toast.success(t("edit_panel.success_update"));
      onSaved(updated);
      onClose();
    } catch {
      toast.error(t("edit_panel.error_update"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {t("edit_panel.title")}
            </h2>
            <p className="mt-0.5 text-xs text-gray-400">{monitor.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {/* Name */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("edit_panel.label_name")}
              </label>
              <TextInput
                required
                value={form.name}
                onValueChange={(v) => set("name", v)}
              />
            </div>

            {/* Type (read-only) */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("edit_panel.label_type")}
              </label>
              <TextInput disabled value={monitor.type} />
            </div>

            {/* Target */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("edit_panel.label_target")}
              </label>
              <TextInput
                required
                value={form.target}
                onValueChange={(v) => set("target", v)}
              />
            </div>

            {/* Interval */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("edit_panel.label_interval")}
              </label>
              <IntervalSelect
                value={form.interval_seconds}
                onChange={(v) => set("interval_seconds", v)}
              />
            </div>

            {/* Confirmation checks */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("edit_panel.label_confirmation")}
              </label>
              <select
                value={form.options.confirmation_count ?? ""}
                onChange={(e) =>
                  setOpt(
                    "confirmation_count",
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
              >
                <option value="">{t("edit_panel.confirmation_default")}</option>
                <option value="1">{t("edit_panel.confirmation_1")}</option>
                <option value="2">{t("edit_panel.confirmation_2")}</option>
                <option value="3">{t("edit_panel.confirmation_3")}</option>
                <option value="5">{t("edit_panel.confirmation_5")}</option>
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {t("edit_panel.confirmation_hint")}
              </p>
            </div>

            {/* Type-specific options */}
            {monitor.type === "http" && (
              <HttpOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "ssl" && (
              <SslOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "domain" && (
              <DomainOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "dns" && (
              <DnsOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "tcp" && (
              <TcpOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "ping" && (
              <PingOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "port" && (
              <PortOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "api" && (
              <ApiOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "cron" && (
              <CronOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "playwright" && (
              <PlaywrightOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "shopify" && (
              <ShopifyOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "wordpress" && (
              <WordPressOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "seo_health" && (
              <SeoHealthOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "broken_links" && (
              <BrokenLinksOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "image_optimization" && (
              <ImageOptimizationOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "performance_budget" && (
              <PerformanceBudgetOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "suspicious_script" && (
              <SuspiciousScriptOptions options={form.options} onChange={setOpt} />
            )}
            {monitor.type === "carbon_footprint" && (
              <CarbonFootprintOptions options={form.options} onChange={setOpt} />
            )}

            {/* Locations — hidden for cron (server-side check) */}
            {monitor.type !== "cron" && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t("edit_panel.label_locations")}
                </label>
                <div className="mt-1 grid grid-cols-2 gap-1.5">
                  {availableLocations.map((loc) => (
                    <label
                      key={loc.slug}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={form.locations.includes(loc.slug)}
                        onChange={(e) =>
                          set(
                            "locations",
                            e.target.checked
                              ? [...form.locations, loc.slug]
                              : form.locations.filter((x) => x !== loc.slug)
                          )
                        }
                        className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                      />
                      <span className="font-mono text-xs">{loc.label}</span>
                    </label>
                  ))}
                </div>
                {form.locations.length === 0 && (
                  <p className="mt-1 text-xs text-red-500">
                    {t("edit_panel.location_required")}
                  </p>
                )}
              </div>
            )}

            {/* Contact groups */}
            {allGroups.length > 0 && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t("edit_panel.label_channels")}
                </label>
                <div className="mt-1 space-y-1">
                  {allGroups.map((g) => (
                    <label
                      key={g.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroupIds.includes(String(g.id))}
                        onChange={() => toggleGroup(g.id)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                      />
                      <span>{g.name}</span>
                      {g.member_count != null && (
                        <span className="text-xs text-gray-400">
                          {t("edit_panel.members_count", { count: g.member_count })}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Escalation policy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("edit_panel.label_escalation")}
              </label>
              <select
                value={form.escalation_policy_id ?? ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    escalation_policy_id: e.target.value || null,
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
              >
                <option value="">{t("edit_panel.escalation_none")}</option>
                {policies.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {policies.length === 0 && (
                <p className="mt-1 text-xs text-gray-400">
                  {t("edit_panel.escalation_upsell")}
                </p>
              )}
            </div>

            {/* Dependency — suppress this monitor's alerts when the parent is down */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Depends on
              </label>
              <select
                value={form.depends_on_monitor_id ?? ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    depends_on_monitor_id: e.target.value || "",
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
              >
                <option value="">No dependency</option>
                {otherMonitors.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                When the parent monitor has a live incident, this monitor still records
                failures but suppresses its alert notifications. Useful for "HTTP
                depends on DNS" so an upstream outage doesn't fan out dozens of
                redundant alerts.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>
              {t("edit_panel.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                saving || (monitor.type !== "cron" && form.locations.length === 0)
              }
            >
              {saving ? t("edit_panel.saving") : t("edit_panel.save")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

export default EditMonitorPanel;
